import { Text } from 'app/design/primitives/text';
import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

interface AnimatedFlameProps {
  /** Outer circle size (px). Default 56. */
  size?: number;
  /** Emoji font size (px). Defaults to size/2. */
  fontSize?: number;
  /** Color of the pulsing halo behind the flame. Default warm orange. */
  haloColor?: string;
  /** Background color of the inner disc holding the emoji. Default white. */
  discColor?: string;
  /** Disable looping animations (mount-bounce only). Useful on low-end devices/web. */
  reduceMotion?: boolean;
  /** When this number changes, plays a one-shot "punch" scale (used on milestone). */
  punchTrigger?: number;
}

export function AnimatedFlame({
  size = 56,
  fontSize,
  haloColor = '#FF8A3D',
  discColor = '#FFFFFF',
  reduceMotion = false,
  punchTrigger = 0,
}: AnimatedFlameProps) {
  const scale = useSharedValue(0);
  const rotate = useSharedValue(0);
  const haloScale = useSharedValue(1);
  const haloOpacity = useSharedValue(0);

  useEffect(() => {
    if (reduceMotion) {
      scale.set(
        withSequence(withSpring(1.25, { damping: 6, stiffness: 140 }), withSpring(1, { damping: 9, stiffness: 120 })),
      );
    } else {
      scale.set(
        withSequence(
          withSpring(1.25, { damping: 6, stiffness: 140 }),
          withSpring(1, { damping: 9, stiffness: 120 }),
          withDelay(500, withRepeat(withTiming(1.08, { duration: 900, easing: Easing.inOut(Easing.ease) }), -1, true)),
        ),
      );
      rotate.set(withRepeat(withTiming(1, { duration: 1400, easing: Easing.inOut(Easing.ease) }), -1, true));
      haloScale.set(withRepeat(withTiming(1.35, { duration: 1200, easing: Easing.inOut(Easing.ease) }), -1, true));
      haloOpacity.set(withRepeat(withTiming(0.55, { duration: 1200, easing: Easing.inOut(Easing.ease) }), -1, true));
    }

    if (punchTrigger !== 0) {
      scale.set(
        withSequence(withSpring(1.5, { damping: 5, stiffness: 200 }), withSpring(1, { damping: 8, stiffness: 120 })),
      );
    }
  }, [haloOpacity, haloScale, punchTrigger, reduceMotion, rotate, scale]);

  const flameStyle = useAnimatedStyle(() => ({
    transform: [
      { scale: scale.get() },
      { rotate: `${String(interpolate(rotate.get(), [0, 0.5, 1], [-4, 4, -4]))}deg` },
    ],
  }));

  const haloStyle = useAnimatedStyle(() => ({
    opacity: haloOpacity.get(),
    transform: [{ scale: haloScale.get() }],
  }));

  const halo2Style = useAnimatedStyle(() => ({
    opacity: haloOpacity.get() * 0.6,
    transform: [{ scale: haloScale.get() * 1.15 }],
  }));

  const dimensions = { width: size, height: size, borderRadius: size / 2 };
  const emojiSize = fontSize ?? Math.round(size * 0.5);

  return (
    <View style={[styles.wrapper, dimensions]}>
      <Animated.View
        pointerEvents="none"
        style={[styles.halo, dimensions, { backgroundColor: haloColor }, halo2Style]}
      />
      <Animated.View
        pointerEvents="none"
        style={[styles.halo, dimensions, { backgroundColor: haloColor }, haloStyle]}
      />
      <View style={[styles.disc, dimensions, { backgroundColor: discColor }]}>
        <Animated.View style={flameStyle}>
          <Text style={{ fontSize: emojiSize, lineHeight: emojiSize * 1.1 }}>🔥</Text>
        </Animated.View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  disc: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  halo: {
    position: 'absolute',
  },
  wrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
